import { Controller } from '../utils/controller';
import Swiper from 'swiper';
import { Autoplay, Navigation, Pagination, Thumbs, Zoom } from 'swiper/modules';
import type { SwiperOptions } from 'swiper/types';


export default class extends Controller {
  static targets = [
    'thumbnail',
    'pagination',
    'thumbnailLeftNav',
    'thumbnailRightNav',
    'thumbnailPagination',
    'leftNav',
    'rightNav',
    'slide',
    'zoomKeyboard'
  ];
  static requiredTargets = [];

  static values = {
    slidesId: String,
    slidesPerViewThumbnail: { type: Number, default: 5 },
    slidesSpaceBetweenThumbnail: { type: Number, default: 10 },
    slidesAutoHeight: { type: Boolean, default: false },
    slidesAutoPlay: { type: Boolean, default: false },
    slidesPerViewXxl: { type: Number, default: 4 },
    slidesPerViewXl: { type: Number, default: 4 },
    slidesPerViewL: { type: Number, default: 3 },
    slidesPerViewM: { type: Number, default: 2 },
    slidesPerViewS: { type: Number, default: 1 },
    slidesPerViewXs: { type: Number, default: 1 },
    slidesPerGroupXl: { type: Number, default: 1 },
    slidesPerGroupL: { type: Number, default: 1 },
    slidesPerGroupM: { type: Number, default: 1 },
    slidesPerGroupS: { type: Number, default: 1 },
    slidesSpaceBetween: { type: Number, default: 10 },
    slidesLoop: { type: Boolean, default: false },
    slidesLoopDesktop: { type: String, default: 'default' },
    thumbsSlidesDirection: { type: String, default: 'vertical'},
    thumbsSlidesPerGroup: { type: Number, default: 1 },
    thumbsSlidesPerView: { type: String, default: 'auto' },
    thumbsSpaceBetween: { type: Number, default: 0 },
    thumbsPaginationType: {type: String, default: 'bullets'},
    thumbsTranslate: { type: Number, default: 0 },
    // NOTE: When the transition to `proper_components` is complete, this can be
    // removed.
    slidesLcId: { type: String, default: '' },
    slidesTrackActive: { type: Boolean, default: false },
    slidesTrackActiveTarget: { type: String, default: '' },
    slidesStartAt: { type: Number, default: 0 },
    slidesSyncCarouselId: String,
    zoomEnabled: { type: Boolean, default: false },
    zoomEnabledBasicConfigs: { type: Boolean, default: false }
  };

  // TARGETS
  declare thumbnailTarget: HTMLElement;
  declare thumbnailLeftNavTarget: HTMLElement;
  declare thumbnailRightNavTarget: HTMLElement;
  declare thumbnailPaginationTarget: HTMLElement;
  declare paginationTarget: HTMLElement;
  declare leftNavTarget: HTMLElement;
  declare rightNavTarget: HTMLElement;
  declare slideTarget: HTMLElement;
  declare zoomKeyboardTarget: HTMLElement;
  declare readonly hasThumbnailTarget: boolean;
  declare readonly hasThumbnailLeftNavTarget: boolean;
  declare readonly hasThumbnailRightNavTarget: boolean;
  declare readonly hasThumbnailPaginationTarget: boolean;
  declare readonly hasPaginationTarget: boolean;
  declare readonly hasLeftNavTarget: boolean;
  declare readonly hasRightNavTarget: boolean;
  declare readonly hasSlideTarget: boolean;
  declare readonly hasZoomKeyboardTarget: boolean;

  // VALUES
  declare slidesPerViewThumbnailValue: number;
  declare slidesSpaceBetweenThumbnailValue: number;
  declare slidesAutoHeightValue: boolean;
  declare slidesAutoPlayValue: boolean;
  declare slidesIdValue: string;
  declare slidesPerViewXxlValue: number;
  declare slidesPerViewXlValue: number;
  declare slidesPerViewLValue: number;
  declare slidesPerViewMValue: number;
  declare slidesPerViewSValue: number;
  declare slidesPerViewXsValue: number;
  declare slidesPerGroupXlValue: number;
  declare slidesPerGroupLValue: number;
  declare slidesPerGroupMValue: number;
  declare slidesPerGroupSValue: number;
  declare slidesSpaceBetweenValue: number;
  declare slidesLoopValue: boolean;
  declare slidesLoopDesktopValue: string;
  declare slidesLcIdValue: string;
  declare slidesSyncCarouselIdValue: string;
  declare slidesTrackActiveValue: boolean;
  declare slidesTrackActiveTargetValue: string;
  declare slidesStartAtValue: number;
  declare thumbsSlidesPerGroupValue: number;
  declare thumbsSlidesPerViewValue: number | 'auto' | undefined;
  declare thumbsSlidesDirectionValue: 'horizontal' | 'vertical' | undefined;
  declare thumbsSpaceBetweenValue: string | number | undefined;
  declare thumbsTranslateValue: number;
  declare thumbsPaginationTypeValue: 'progressbar' | 'bullets' | 'fraction' | 'custom';
  declare zoomEnabledValue: boolean;
  declare zoomEnabledBasicConfigsValue: boolean;
  declare readonly hasSlidesIdValue: boolean;
  declare readonly hasSlidesSyncCarouselIdValue: boolean;
  declare readonly hasThumbsTranslateValue: boolean;

  // LOCAL STATE
  private slide: Swiper;
  private resizeObserver;
  private intersectionObserver;

  thumbnails() {
    // short circuit the thumbs config
    if (!this.hasThumbnailTarget) {
      return {};
    }

    const thumbsSlider = new Swiper(this.thumbnailTarget, {
      modules: [Navigation, Pagination],
      slidesPerView: this.thumbsSlidesPerViewValue,
      slidesPerGroup: this.thumbsSlidesPerGroupValue || 1,
      spaceBetween: this.thumbsSpaceBetweenValue,
      direction: this.thumbsSlidesDirectionValue,
      threshold: 5,
      pagination: this.thumbnailPagination(),
      navigation: {
        nextEl: this.hasThumbnailRightNavTarget ? this.thumbnailRightNavTarget : null,
        prevEl: this.hasThumbnailLeftNavTarget ? this.thumbnailLeftNavTarget : null
      },
      lazyPreloadPrevNext: 1
    });

    if (this.hasThumbsTranslateValue) {
      thumbsSlider.setTranslate(this.thumbsTranslateValue);
    }

    return {
      swiper: thumbsSlider
    };
  }

  updateThumbnailNavigation(): void {
    const swiper = this.slide?.thumbs?.swiper;

    if (!swiper || !this.hasThumbnailRightNavTarget || !this.hasThumbnailLeftNavTarget) {
      return;
    }

    const navigation = swiper.params?.navigation as
      | { nextEl?: HTMLElement; prevEl?: HTMLElement }
      | undefined;

    if (typeof navigation === 'object' && navigation !== null) {
      navigation.nextEl = this.thumbnailRightNavTarget;
      navigation.prevEl = this.thumbnailLeftNavTarget;
      swiper.navigation.init();
      swiper.navigation.update();
    }
  }

  pagination() {
    return {
      el: this.hasPaginationTarget ? this.paginationTarget : null,
      clickable: this.hasPaginationTarget
    };
  }

  thumbnailPagination() {
    return {
      el: this.hasThumbnailPaginationTarget ? this.thumbnailPaginationTarget : null,
      type: this.thumbsPaginationTypeValue
    };
  }

  autoplay() {
    if (!this.slidesAutoPlayValue) {
      return false;
    }

    return {
      delay: 3500,
      disableOnInteraction: false,
      pauseOnMouseEnter: true
    };
  }

  navigation() {
    return {
      nextEl: this.hasRightNavTarget ? this.rightNavTarget : null,
      prevEl: this.hasLeftNavTarget ? this.leftNavTarget : null
    };
  }

  setDesktopLoopBreakpoint() {
    if (this.slidesLoopDesktopValue === 'false') {
      return false;
    }

    return this.slidesLoopValue;
  }

  breakpoints() {
    return {
      // when window width is >= 640px
      640: {
        slidesPerView: this.slidesPerViewSValue,
        slidesPerGroup: this.slidesPerGroupSValue,
        loop: this.setDesktopLoopBreakpoint()
      },
      // when window width is >= 768px
      768: {
        slidesPerView: this.slidesPerViewMValue,
        slidesPerGroup: this.slidesPerGroupMValue,
        loop: this.setDesktopLoopBreakpoint()
      },
      // when window width is >= 1024px
      1024: {
        slidesPerView: this.slidesPerViewLValue,
        slidesPerGroup: this.slidesPerGroupLValue,
        loop: this.setDesktopLoopBreakpoint()
      },
      // when window width is >= 1280px
      1280: {
        slidesPerView: this.slidesPerViewXlValue,
        slidesPerGroup: this.slidesPerGroupXlValue,
        loop: this.setDesktopLoopBreakpoint()
      },
      // when window width is >= 1440px
      1440: {
        slidesPerView: this.slidesPerViewXxlValue,
        slidesPerGroup: this.slidesPerGroupXlValue,
        loop: this.setDesktopLoopBreakpoint()
      }
    };
  }

  adjustArrows = () => {
    const image = this.element.querySelector('.swiper-slide img');
    const leftArrow = this.hasLeftNavTarget ? this.leftNavTarget : null;
    const rightArrow = this.hasRightNavTarget ? this.rightNavTarget : null;

    if (image && leftArrow && rightArrow) {
      const imageRect = image.getBoundingClientRect();
      const carouselRect = this.element.getBoundingClientRect();

      const imageCenter = imageRect.top + imageRect.height / 2;
      const carouselTop = carouselRect.top;

      const offsetTop = imageCenter - carouselTop;

      const element = this.element as HTMLElement;

      (this.element as HTMLElement).style.setProperty('--pincer-swiper-navigation-top-offset', `${offsetTop}px`);
    }
  };

  alignArrows() {
    const image = this.element.querySelector('.swiper-slide img') as HTMLImageElement | null;

    if (image) {
      if (image.complete && image.naturalHeight !== 0) {
        this.adjustArrows();
      } else {
        image.addEventListener('load', this.adjustArrows);
      }
    }
  }

  disconnect() {
    // Clean up event listeners and observers
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }

  async mount(slideTarget: HTMLElement) {
    if (!this.hasSlideTarget) {
      return null;
    }

    const config = this.buildSwiperConfig()
    const swiper = new Swiper(slideTarget, config);
    this.slide = swiper;

    if (this.zoomEnabled()) {
      // Preload the zoomed image on the active slide
      const activeSlide = this.findActiveSlide();
      if (activeSlide) {
        const imageEl = activeSlide.querySelector<HTMLImageElement>('.swiper-zoom-container img.carousel-image');
        if (imageEl && imageEl.dataset.zoomSrc) {
            await this.replaceSrcWithZoom(imageEl)
            this.attachZoomEvents();
        }
      }
    }

    if (this.hasSlidesIdValue) {
      window[this.slidesIdValue] = swiper
    }

    if (this.slidesTrackActiveValue) {
      this.slide.on('slideChange', () => {
        this.slide.thumbs.swiper.slideTo(this.slide.activeIndex);
        if (this.hasSlidesSyncCarouselIdValue && window[this.slidesSyncCarouselIdValue]) {
          window[this.slidesSyncCarouselIdValue].slideTo(swiper.activeIndex, 0)
        }
        if (this.slidesLcIdValue) {
          window.dispatchEvent(
            new CustomEvent('vulcan:hook-relay:push-event-to', {
              detail: {
                selectorOrTarget: `#${this.slidesLcIdValue}`,
                event: 'update_live_active_slide',
                payload: {
                  slide: this.slide.activeIndex,
                  thumbs_translate: this.slide.thumbs.swiper.translate || 0
                }
              }
            })
          );
        }
      });

      this.slide.on('observerUpdate', () => {
        this.slide.update();
        this.slide.thumbs.swiper.setTranslate(this.slide.thumbs.swiper.translate);
        this.slide.thumbs.swiper.update();
        this.updateThumbnailNavigation();
      });
    }

    const verticallyAlignArrowsOnImageValue = this.containsClass(
      'slide',
      'valign-arrows-with-image'
    );

    if (verticallyAlignArrowsOnImageValue) {
      this.alignArrows();

      this.resizeObserver = new ResizeObserver(() => {
        this.adjustArrows();
      });
      this.resizeObserver.observe(slideTarget);
    }

    return this.slide;
  }

  initialize() {
    if (!this.hasSlideTarget) {
      return null;
    }
    const handleIntersection = (
      entries: IntersectionObserverEntry[],
      observer: IntersectionObserver
    ) => {
      entries.forEach((entry: IntersectionObserverEntry) => {
        if (entry.isIntersecting) {
          const target = entry.target as HTMLElement;
          if (!target.classList.contains('swiper-observer-initialized')) {
            this.mount(target);
            target.classList.add('swiper-observer-initialized');
          }

          observer.unobserve(target);
        }
      });
    };

    this.intersectionObserver = new IntersectionObserver(handleIntersection, {
      root: null,
      rootMargin: '200px',
      threshold: 0
    });
    this.intersectionObserver.observe(this.slideTarget);
  }

  private zoomEnabled() {
    return this.zoomEnabledValue;
  }

  private attachZoomEvents() {
    // Triggers zoom when Enter or Space is pressed while zoomKeyboardTarget is focused
    if (this.hasZoomKeyboardTarget) {
      this.zoomKeyboardTarget.addEventListener('keydown', (event: KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          if (this.slide?.zoom) {
            this.slide.zoom.toggle();
          }
        }
      });
    }

    this.slide.slides.forEach((slideEl) => {      
      slideEl.addEventListener('mouseenter', async () => {
        const imageEl = slideEl.querySelector<HTMLImageElement>('.swiper-zoom-container img.carousel-image');
        if (imageEl && imageEl.dataset.zoomSrc) {
          try {
            await this.replaceSrcWithZoom(imageEl);
            this.slide.zoom.in();
          } catch (error) {
            console.error('Failed to load image:', error);
          }
        }
      });

      // Triggers zoom-out when the mouse leaves the slide
      slideEl.addEventListener('mouseleave', () => this.slide.zoom.out());
    });
  }

  async replaceSrcWithZoom(imageEl: HTMLImageElement): Promise<void> {
    const zoomSrc = imageEl.dataset.zoomSrc;

    if (!zoomSrc) {
      return;
    }

    if (imageEl.src === zoomSrc) {
      if (imageEl.complete) {
        return;
      }

      // Wait for loading if still in progress, without re-assigning src
      return new Promise((resolve, reject) => {
        imageEl.onload = () => resolve();
        imageEl.onerror = (err) => reject(err);
      });
    }

    // Different src: assign and wait for load/error
    return new Promise((resolve, reject) => {
      imageEl.onload = () => resolve();
      imageEl.onerror = (err) => reject(err);
      imageEl.src = zoomSrc;
    });
  }

  private findActiveSlide() {
    return this.slide.slides.find(el => el.classList.contains('swiper-slide-active'));
  }

  private buildSwiperConfig() {
    const initialSlide = window[this.slidesSyncCarouselIdValue]?.activeIndex ?? this.slidesStartAtValue;

    let baseConfig: SwiperOptions = {
      modules: [Autoplay, Navigation, Pagination, Thumbs],
      navigation: this.navigation(),
      autoHeight: this.slidesAutoHeightValue,
      slidesPerView: this.slidesPerViewXsValue,
      slidesPerGroup: 1,
      spaceBetween: this.slidesSpaceBetweenValue,
      pagination: this.pagination(),
      thumbs: this.thumbnails(),
      loop: this.slidesLoopValue,
      autoplay: this.autoplay(),
      observer: true,
      breakpoints: this.breakpoints(),
      initialSlide: initialSlide,
      threshold: 10,
      lazyPreloadPrevNext: 1
    };

    if (this.zoomEnabled()) {
      baseConfig = {
        ...baseConfig,
        modules: [...baseConfig.modules!, Zoom],
        zoom: this.zoomEnabledBasicConfigsValue || {
          toggle: false,
          maxRatio: 2,
          panOnMouseMove: true
        }
      };
    }

    return baseConfig;
  }

  private containsClass(targetName: string, className: string): boolean {
    const targetElement = this[targetName + 'Target'];
    return targetElement.classList.contains(className);
  }
}
